import axios from "axios";
import moment from "moment";

export class NetworkService {
  constructor() {
    const rawUrl = process.env.REACT_APP_API_URL || "https://api.appcargodev.com";
    this.apiBaseUrl = String(rawUrl).replace(/\/company-api\/?$/, "").replace(/\/+$/, "");
    this.url = `${this.apiBaseUrl}/company-api/`;
    this.jwtToken = this._readTokenFromStorage();
    this.handle401 = null;
    this.handle403 = null;
    this._handleErrors = this._handleErrors.bind(this);
    this._handleResponse = this._handleResponse.bind(this);
  }

  _readTokenFromStorage() {
    try {
      const rawUser = localStorage.getItem("user");
      if (!rawUser || rawUser === "null") return null;

      const user = JSON.parse(rawUser);
      return user?.authorisation?.token ?? null;
    } catch {
      return null;
    }
  }

  setAuthToken(token) {
    this.jwtToken = token ?? null;
  }

  _buildUrl(baseUrl, path) {
    const base = String(baseUrl).replace(/\/+$/, "");
    const normalizedPath = String(path).replace(/^\/+/, "");
    return `${base}/${normalizedPath}`;
  }

  _companyAdminBaseUrl() {
    return this.apiBaseUrl;
  }

  _makeRequest(method, path, config = {}, auth = false, baseUrl = this.url) {
    let { headers, body, params, ...rest } = config;
    let additionalHeaders = {};
    if ((method === "POST" || method === "PUT") && typeof body === "object" && !(body instanceof FormData)) {
      additionalHeaders["content-type"] = "application/json";
      body = JSON.stringify(body);
    }
    if (this.jwtToken) {
      additionalHeaders["Authorization"] = "Bearer " + this.jwtToken;
    }

    return axios({
      method: method,
      url: this._buildUrl(baseUrl, path),
      ...config,
      headers: {
        ...additionalHeaders,
        ...headers,
      },
    })
      .then(this._handleResponse)
      .catch(this._handleErrors);
  }

  // dodaj u NetworkService.js

  _makeExternalRequest(method, path, config = {}, baseUrl) {
    let { headers, body, params, ...rest } = config;
    let additionalHeaders = {};
    if ((method === "POST" || method === "PUT") && typeof body === "object" && !(body instanceof FormData)) {
      additionalHeaders["content-type"] = "application/json";
      body = JSON.stringify(body);
    }

    return axios({
      method: method,
      url: `${baseUrl}/${path}`,
      ...config,
      headers: {
        ...headers,
        ...additionalHeaders,
      },
    })
      .then(this._handleResponse)
      .catch(this._handleErrors);
  }

  // Reverse geocoding
  reverseGeocode(lat, lng, format = "json", addressdetails = 1, zoom = 16) {
    const baseUrl = "https://api.appcargo.com/direction/geocoder/reverse";

    return this._makeExternalRequest(
      "POST",
      "",
      {
        data: { lat, lng, format, addressdetails, zoom },
      },
      baseUrl
    );
  }
  
  // Geocoding
  geocode(query, lang = "SR", city = "Belgrade", countryCodes = "", bounds = "", limit = 10) {
    const baseUrl = "https://api.appcargo.com/direction/geocoder/geocode";
    return this._makeExternalRequest(
      "POST",
      "",
      {
        data: { query, lang, city, countryCodes, bounds, limit },
      },
      baseUrl
    );
  }

  _handleErrors(error) {
    if (error?.response?.status === 401 && this.jwtToken) {
      this.jwtToken = null;
      localStorage.removeItem("user");
      window.location.reload();
    }
    console.log(error);
    throw error.response;
  }

  async _handleResponse(response) {
    let body = null;
    let contentType = response.headers["content-type"];
    if (contentType && contentType.startsWith("application/json")) {
      body = await response.data;
    } else {
      return response;
    }
    return body;
  }

  getRides(from, to, pagination, search) {
    return this._makeRequest("GET", `company/drives`, {
      params: {
        from: from,
        to: to,
        perPage: pagination?.size,
        page: pagination?.page,
        search: search,
      },
    });
  }
  getRidesForUser(userId, from, to, pagination, search) {
    return this._makeRequest("GET", `company/drives/${userId}`, {
      params: {
        from: from,
        to: to,
        perPage: pagination?.size,
        page: pagination?.page,
        search: search,
      },
    });
  }

  getUsers(pagination, search) {
    return this._makeRequest("GET", `company/users`, {
      params: {
        perPage: pagination?.size,
        page: pagination?.page,
        search: search,
      },
    });
  }

  getUserById(id) {
    return this._makeRequest("GET", `company/users/${id}`, {});
  }
  addUser(userModalData) {
    return this._makeRequest("POST", `company/users`, {
      data: userModalData,
    });
  }

  deleteUser(id) {
    return this._makeRequest("PUT", `company/users/delete`, {
      data: {
        user_id: id,
      },
    });
  }

  editUser(data) {
    return this._makeRequest("PUT", `company/users`, {
      data: {
        user_id: data.id,
        ...data,
      },
    });
  }

  editUserEmployeeDetails(userModalData) {
    return this._makeRequest("POST", `company/users/update-employee-details`, {
      data: {
        user_id: userModalData?.id,
        employee_details: userModalData?.employee_details,
      },
    });
  }
  editUserSpendingLimit(userModalData) {
    return this._makeRequest("POST", `company/users/update-spending-limit`, {
      data: {
        user_id: userModalData?.id,
        limit: userModalData?.limit,
      },
    });
  }

  bulkUpdateUsers(userIds, allowedAddresses) {
    return this._makeRequest("PUT", `company/users/bulk-update`, {
      data: {
        user_ids: userIds,
        allowed_address: allowedAddresses,
      },
    });
  }

  login(formData) {
    return this._makeRequest("POST", `auth/login`, {
      data: {
        email: formData?.email,
        password: formData?.password,
      },
    });
  }
  registerCompany(data) {
    return this._makeRequest("POST", `auth/create`, {
      data: data
    });
  }
  registerAdmin(data) {
    return this._makeRequest("POST", `auth/create-admin`, {
      data: data
    });
  }
  forgotPassword(email) {
    return this._makeRequest("POST", `auth/reset-password`, {
      data: {
        email: email,
      },
    });
  }
  resetPassword(password, token) {
    return this._makeRequest("POST", `auth/handle-reset-password`, {
      data: {
        password: password,
      },

      headers: {
        Authorization: "Bearer " + token,
      },
    });
  }

  editCompanyCreditLowLimit(credit) {
    return this._makeRequest("POST", `company/set-credit-low-limit`, {
      data: {
        credit: credit,
      },
    });
  }
  requestCompanyCredit(credit) {
    return this._makeRequest("POST", `company/request-credit`, {
      data: {
        credit: credit,
      },
    });
  }

  getTransactions(pagination, search) {
    return this._makeRequest("GET", `company/transactions`, {
      params: {
        perPage: pagination?.size,
        page: pagination?.page,
        search: search,
      },
    });
  }

  getTodaysData() {
    return this._makeRequest("GET", `company`, {});
  }
  getCompanyAddresses() {
    return this._makeRequest("GET", `company/suggested-addresses`, {});
  }

  getFavoriteAddresses() {
    return this._makeRequest("GET", "company/favorite-addresses", {});
  }

  getMonthlyReport(formData) {
    const month = moment(formData.date).get("M");
    const year = moment(formData.date).get("y");
    const email = formData.email;

    return this._makeRequest("POST", `company/monthly-report`, {
      data: {
        month: month + 1,
        year: year,
        email: email,
      },
    });
  }
  getAllInvoices(formData) {
    const month = moment(formData.date).get("M");
    const year = moment(formData.date).get("y");
    const email = formData.email;

    return this._makeRequest("POST", `company/download-fiscal-invoices`, {
      params: {
        month: month + 1,
        year: year,
      },
      data: {
        email: email,
      },
    });
  }

  getAllInvoicesForUser(userId, formData) {
    const month = moment(formData.date).get("M");
    const year = moment(formData.date).get("y");
    const email = formData.email;

    return this._makeRequest("POST", `company/download-fiscal-invoices/${userId}`, {
      params: {
        month: month + 1,
        year: year,
      },
      data: {
        email: email,
      },
    });
  }
  getContract(formData) {
    return this._makeRequest("POST", `company/contract`, {
      data: {
        email: formData?.responsiblePersonEmail,
        responsible_person: formData?.responsiblePerson,
        position: formData?.responsiblePersonPosition,
      },
    });
  }
  getAnnex(formData) {
    return this._makeRequest("POST", `company/annex`, {
      responseType: "blob",
      data: {
        responsible_person: formData?.responsiblePerson,
        position: formData?.responsiblePersonPosition,
      },
    });
  }

  getPersonalDataAgreement(formData) {
    console.log(formData);
    return this._makeRequest("POST", `company/personal-data-agreement`, {
      responseType: "blob",
      data: {
        responsible_person: formData?.responsiblePerson,
        responsible_person_email: formData?.responsiblePersonEmail,
      },
    });
  }

  getInvoices(formData) {
    return this._makeRequest("GET", `company/invoices`, {
      params: formData,
    });
  }
  downloadInvoice(data) {
    return this._makeRequest("GET", `company/download/invoice`, {
      responseType: "blob",
      params: {
        fleet_id: data?.the_transaction_amount,
        start_date: data?.start_date,
        end_date: data?.end_date,
        period: data?.period,
      },
    });
  }

  getIdentificationForm(formData) {
    return this._makeRequest("GET", `company/download/identification-form`, {
      responseType: "blob",
    });
  }

  estimateCompanyRidePrice(payload, companyAdminId) {
    return this._makeRequest(
      "POST",
      "company-admin/estimate",
      {
        data: payload,
        headers: {
          "X-system-company-admin-id": String(companyAdminId),
        },
      },
      false,
      this._companyAdminBaseUrl()
    );
  }

  createCompanyRideRequest(payload, companyAdminId) {
    return this._makeRequest(
      "POST",
      "company-admin/request",
      {
        data: payload,
        headers: {
          "X-system-company-admin-id": String(companyAdminId),
        },
      },
      false,
      this._companyAdminBaseUrl()
    );
  }

  getCompanyRideTrackingUrl(rideId, companyAdminId) {
    return this._makeRequest(
      "GET",
      `company-admin/rides/${rideId}/tracking-url`,
      {
        headers: {
          "X-system-company-admin-id": String(companyAdminId),
        },
      },
      false,
      this._companyAdminBaseUrl()
    );
  }

  cancelCompanyRide(rideId, companyAdminId) {
    return this._makeRequest(
      "POST",
      `company-admin/rides/${rideId}/cancel`,
      {
        headers: {
          "X-system-company-admin-id": String(companyAdminId),
        },
      },
      false,
      this._companyAdminBaseUrl()
    );
  }

  getUpcomingScheduledRides(companyAdminId) {
    return this._makeRequest(
      "GET",
      "company-api/company/active-rides",
      {
        headers: {
          "X-system-company-admin-id": String(companyAdminId),
        },
      },
      false,
      this._companyAdminBaseUrl()
    );
  }
}

export default new NetworkService();
